import { AiDisclosureBadge, type AiDisclosureAlign } from '../AiDisclosureBadge/AiDisclosureBadge'
import './MediaImage.css'

export interface MediaImageProps {
  src: string
  /**
   * Absent pour une image qui n'existe qu'en une seule taille — c'est le cas
   * des photos embarquées à la compilation, servies en repli quand l'API ne
   * répond pas. Un `srcset` vide serait pire qu'aucun.
   */
  srcSet?: string
  sizes?: string
  /** Description complète, mention de signalement comprise s'il y en a une. */
  alt: string
  /** `object-position` calculé depuis le point focal du média. */
  objectPosition: string
  /**
   * Le signalement dû, ou `null` pour une photographie authentique.
   *
   * **Requis, jamais optionnel** : c'est la garantie de l'article 50 dans le
   * système de types. Un signalement omis serait un manquement ; un `null` est
   * une déclaration, et le compilateur oblige à en écrire une. Composé par
   * `mediaImageProps()` côté contrats — ne le renseignez pas à la main.
   */
  disclosure: string | null
  /** Voir `AiDisclosureBadge.align` : `center` pour les angles très arrondis. */
  disclosureAlign?: AiDisclosureAlign
  loading?: 'lazy' | 'eager'
}

/**
 * Tout visuel piloté par le back-office passe par ici : c'est ce composant qui
 * pose le signalement d'origine, pas la page qui l'affiche. Aucun emplacement
 * ne peut donc l'oublier — et c'est exactement le risque que l'obligation de
 * signalement fait courir, puisque le tag seul, en base, ne protège de rien.
 *
 * Le badge est retiré de l'arbre d'accessibilité : sa mention figure déjà dans
 * l'`alt`, et l'annoncer deux fois n'apprendrait rien de plus.
 */
export function MediaImage({
  src,
  srcSet,
  sizes,
  alt,
  objectPosition,
  disclosure,
  disclosureAlign = 'start',
  loading = 'lazy',
}: MediaImageProps) {
  return (
    <span className="azoya-media-image">
      <img src={src} srcSet={srcSet} sizes={sizes} alt={alt} loading={loading} style={{ objectPosition }} />
      {disclosure && <AiDisclosureBadge label={disclosure} align={disclosureAlign} hiddenFromScreenReaders />}
    </span>
  )
}
